import { useContext } from 'react';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeContext } from 'styled-components';

import ImageUnsupported from 'images/pictographs/unsupported.svg';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import GovernmentIdOptions from 'inquiry/views/InquiryGovernmentIdSelectView/GovernmentIdOptions';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { colorCss } from 'lib/inquiryStyles';
import { Disclaimer, StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { PictographPosition } from 'lib/interfaces/ThemeSerialized';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import { DisclaimerText } from 'verify/components/Disclaimer';
import ThemedSvg from 'verify/components/ThemedSvg';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div<{
  idClassListStyle: InquiryGovernmentIdSelectViewProps['idClassListStyle'];
  reduceMinHeight?: boolean;
}>`
  margin-bottom: ${SPACINGS.base * 4}px;

  ${({ idClassListStyle, reduceMinHeight }) => {
    switch (idClassListStyle) {
      case 'outline':
        return css`
          min-height: ${reduceMinHeight ? 'max-content' : MIN_HEIGHTS.header};
        `;
      case 'outline_light':
        // This custom theme is specifically for DoorDash.
        return undefined;
    }
  }}
`;

interface InquiryGovernmentIdSelectViewProps {
  assets: StepGovernmentIdConfig['assets']['select-page'];
  disclaimer: Disclaimer;
  handleSelectType: (type: GovernmentIdClass) => void;
  idClasses: StepGovernmentIdConfig['idclasses'];
  idClassListStyle: 'outline' | 'outline_light' | 'button';
  localizations: StepGovernmentIdConfig['localizations']['select-page'];
  selectedCountryCode: StepGovernmentIdConfig['selected-country-code'];
  enableRedesign: boolean;
}

export default function InquiryGovernmentIdSelectView({
  assets,
  disclaimer,
  handleSelectType,
  idClasses,
  idClassListStyle,
  localizations,
  selectedCountryCode,
  enableRedesign,
}: InquiryGovernmentIdSelectViewProps) {
  const { governmentIdPictographPosition, governmentIdSelectPictographUrl } =
    useContext(ThemeContext);
  const { base, selectOption } = useStepGovernmentIdStyleContext();

  const pictographNode = governmentIdSelectPictographUrl ? (
    <CenteredPictograph aria-label='government id prompt'>
      <CustomizablePictograph
        url='governmentIdSelectPictographUrl'
        height='governmentIdSelectPictographHeight'
        aria-label='government id prompt picture'
      />
    </CenteredPictograph>
  ) : null;

  if (idClasses.length > 0) {
    const hasPromptText = localizations.prompt !== '';
    return (
      <Container>
        <CustomAssetWithFallback
          customImage={assets['header-pictograph']}
          alt='header pictograph'
        />

        {governmentIdPictographPosition === PictographPosition.Start && pictographNode}

        <Header idClassListStyle={idClassListStyle} reduceMinHeight={!hasPromptText}>
          <ThemedTitle styles={base?.titleStyle}>
            <Markdown>{localizations.title}</Markdown>
          </ThemedTitle>
          {hasPromptText && (
            <ThemedParagraph styles={base?.textStyle}>
              <Markdown>{localizations.prompt}</Markdown>
            </ThemedParagraph>
          )}
        </Header>

        {governmentIdPictographPosition === PictographPosition.Center && pictographNode}

        <GovernmentIdOptions
          assets={assets}
          styles={base?.textStyle}
          optionStyles={selectOption}
          idClasses={idClasses}
          idClassListStyle={idClassListStyle}
          localizations={localizations}
          selectedCountryCode={selectedCountryCode}
          handleSelectType={handleSelectType}
          enableRedesign={enableRedesign}
        />

        {/*Stuff like this is hard to translate. Does it belong in the gov ID step config?*/}
        {disclaimer !== Disclaimer.None && <DisclaimerText styles={base?.disclaimerStyle} />}
      </Container>
    );
  } else {
    const hasPromptTextForUnsupportedView = localizations.prompt !== '';
    return (
      <Container>
        <CustomAssetWithFallback
          customImage={assets['header-pictograph']}
          alt='header pictograph'
        />

        <Header
          idClassListStyle={idClassListStyle}
          reduceMinHeight={!hasPromptTextForUnsupportedView}
        >
          <ThemedTitle styles={base?.titleStyle}>
            <Markdown>{localizations['unsupported-title']}</Markdown>
          </ThemedTitle>
          {hasPromptTextForUnsupportedView && (
            <ThemedParagraph styles={base?.textStyle}>
              <Markdown>{localizations['unsupported-prompt']}</Markdown>
            </ThemedParagraph>
          )}
        </Header>
        <CenteredPictograph aria-label='pictograph indicating no available ID types'>
          <ThemedSvg
            $primaryColor={colorCss(base?.fill?.color)}
            $highlightColor={colorCss(base?.fill?.highlightColor)}
            $backgroundColor={colorCss(base?.fill?.backgroundColor)}
            $strokeColor={colorCss(base?.stroke?.color)}
          >
            <ImageUnsupported />
          </ThemedSvg>
        </CenteredPictograph>
      </Container>
    );
  }
}
